import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, ChevronRight, ChevronLeft, Play } from 'lucide-react';

const HERO_METRICS = [
  {
    id: 'modules',
    tabLabel: 'Modules',
    badge: 'ENTERPRISE SUITE',
    count: '8',
    suffix: '+',
    title: 'Integrated Operations Modules',
    subnote: 'Complete workforce engine: talent sourcing, compliance onboarding, timesheets, automated billing & H-1B visa lifecycle.',
    pills: ['ATS Pipelines', 'Bench Sales', 'Timesheets', 'H-1B Visa']
  },
  {
    id: 'approvals',
    tabLabel: 'Timesheets',
    badge: 'WORKFORCE VELOCITY',
    count: '1',
    suffix: '-Click',
    title: 'Instant Supervisor Sign-Offs',
    subnote: 'Weekly timesheet grid submissions with automated manager routing, real-time audit trail and instant PTO sync.',
    pills: ['Weekly Grid', 'Real-Time Audit', 'PTO Sync', '1-Click Approve']
  },
  {
    id: 'compliance',
    tabLabel: 'Compliance',
    badge: 'LEGAL ASSURANCE',
    count: '100',
    suffix: '%',
    title: 'USCIS & DocuSign Compliant',
    subnote: 'Automated I-129 LCA petition tracking, Public Access File (PAF) readiness, and encrypted digital signatures.',
    pills: ['I-129 LCA', 'PAF Ready', 'DocuSign API', 'Status Alerts']
  },
  {
    id: 'billing',
    tabLabel: 'Invoicing',
    badge: 'FINANCIAL ACCURACY',
    count: '0',
    suffix: '%',
    title: 'Billing Discrepancy Rate',
    subnote: 'Direct transformation of approved billable hours into Net-30 compliant corporate invoices with aging tracking.',
    pills: ['Net 30 Sync', 'Auto Aging', 'Zero Error Rate', 'PDF Export']
  }
];

const TYPING_PHRASES = [
  'Total operations control.',
  'Automated onboarding.',
  '1-click timesheet approvals.',
  'Zero-error Net-30 billing.',
  'Complete H-1B compliance.'
];

const DemoHero = () => {
  const navigate = useNavigate();
  const [activeMetricIndex, setActiveMetricIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);

  // Dynamic Typewriter Effect for Hero Headline
  const [typedPhraseIdx, setTypedPhraseIdx] = useState(0);
  const [typedText, setTypedText] = useState('Total operations control.');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentPhrase = TYPING_PHRASES[typedPhraseIdx];
    let timer;

    if (!isDeleting && typedText === currentPhrase) {
      // Pause at full phrase
      timer = setTimeout(() => setIsDeleting(true), 2400);
    } else if (isDeleting && typedText === '') {
      // Move to next phrase
      setIsDeleting(false);
      setTypedPhraseIdx((prev) => (prev + 1) % TYPING_PHRASES.length);
      timer = setTimeout(() => {}, 280);
    } else {
      // Typing or Deleting
      const speed = isDeleting ? 30 : 65;
      timer = setTimeout(() => {
        const next = isDeleting
          ? currentPhrase.substring(0, typedText.length - 1)
          : currentPhrase.substring(0, typedText.length + 1);
        setTypedText(next);
      }, speed);
    }

    return () => clearTimeout(timer);
  }, [typedText, isDeleting, typedPhraseIdx]);

  const activeMetric = HERO_METRICS[activeMetricIndex];

  // Auto-play progress and cycle
  useEffect(() => {
    if (isHovered) return;

    const intervalTime = 50;
    const totalDuration = 3800; // 3.8s per slide
    const increment = (intervalTime / totalDuration) * 100;

    const timer = setInterval(() => {
      setProgressPercent((prev) => {
        if (prev >= 100) {
          setActiveMetricIndex((idx) => (idx + 1) % HERO_METRICS.length);
          return 0;
        }
        return prev + increment;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [isHovered]);

  const selectMetric = (index) => {
    setActiveMetricIndex(index);
    setProgressPercent(0);
  };

  const nextMetric = () => {
    setActiveMetricIndex((prev) => (prev + 1) % HERO_METRICS.length);
    setProgressPercent(0);
  };

  const prevMetric = () => {
    setActiveMetricIndex((prev) => (prev - 1 + HERO_METRICS.length) % HERO_METRICS.length);
    setProgressPercent(0);
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="erp-hero-indigo-section" id="overview">
      <div className="erp-hero-indigo-overlay" />

      <div className="jp-workspace-container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="erp-hero-indigo-grid">
          {/* Left Hero Content: Authoritative & Modern Enterprise Typography */}
          <div className="erp-hero-indigo-intro">
            <div className="erp-hero-indigo-eyebrow">
              <span className="erp-eyebrow-live-dot" />
              <span style={{ fontWeight: 700, letterSpacing: '0.6px' }}>PROPHECY NEXUS</span>
              <span style={{ opacity: 0.35 }}>|</span>
              <span style={{ color: '#cbd5e1' }}>Workforce & Operations Engine</span>
            </div>

            <h1 className="erp-hero-indigo-title">
              <span>One unified platform.</span><br />
              <span className="erp-hero-typed-text">{typedText}</span>
              <span className="erp-hero-typed-cursor">|</span>
            </h1>

            <p className="erp-hero-indigo-lede">
              The complete operational engine for modern staffing, recruitment, and workforce management. Source talent, automate onboarding compliance, approve timesheets, and run automated Net-30 billing in one continuous workflow.
            </p>

            <div style={{ display: 'flex', gap: '12px', marginTop: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="erp-demo-btn-primary"
                onClick={() => scrollToSection('video-demo')}
                style={{ padding: '10px 22px', fontSize: '13.5px' }}
              >
                <Play size={15} />
                <span>Watch 1-Min Video Demo</span>
              </button>

              <button
                type="button"
                className="erp-hero-btn-white-glass"
                onClick={() => scrollToSection('modules-workspace')}
              >
                <span>Explore 8 Modules</span>
                <ArrowRight size={15} />
              </button>
            </div>

            {/* Clean Enterprise Trust Proof Bar */}
            <div className="erp-hero-trust-bar">
              <div className="erp-trust-item">
                <ShieldCheck size={14} color="#10b981" />
                <span>SOC-2 Type II Aligned</span>
              </div>
              <span className="erp-trust-sep">•</span>
              <div className="erp-trust-item">
                <span>100% USCIS & I-9 Audit-Ready</span>
              </div>
              <span className="erp-trust-sep">•</span>
              <div className="erp-trust-item">
                <span>Zero Setup Required</span>
              </div>
            </div>
          </div>

          {/* Right Hero: High-End Glassmorphic Operations Console */}
          <div className="erp-hero-interactive-card-wrap">
            <div
              className="erp-interactive-suite-card"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {/* Subtle Auto-cycle Progress Line */}
              <div className="erp-suite-card-progress-track">
                <div
                  className="erp-suite-card-progress-fill"
                  style={{
                    width: `${progressPercent}%`,
                    transition: isHovered ? 'none' : 'width 60ms linear'
                  }}
                />
              </div>

              {/* Card Header: Segment Tabs & Nav Controls */}
              <div className="erp-suite-top-row">
                <div className="erp-suite-segment-tabs">
                  {HERO_METRICS.map((m, idx) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`erp-suite-tab-pill ${activeMetricIndex === idx ? 'active' : ''}`}
                      onClick={() => selectMetric(idx)}
                    >
                      {m.tabLabel}
                    </button>
                  ))}
                </div>

                <div className="erp-suite-nav-controls">
                  <button
                    type="button"
                    className="erp-suite-nav-arrow"
                    onClick={prevMetric}
                    title="Previous metric"
                    aria-label="Previous metric"
                  >
                    <ChevronLeft size={13} />
                  </button>
                  <span className="erp-suite-nav-counter">
                    {activeMetricIndex + 1}/{HERO_METRICS.length}
                  </span>
                  <button
                    type="button"
                    className="erp-suite-nav-arrow"
                    onClick={nextMetric}
                    title="Next metric"
                    aria-label="Next metric"
                  >
                    <ChevronRight size={13} />
                  </button>
                </div>
              </div>

              {/* Horizontal Body: Left KPI Stat / Right Operational Details */}
              <div
                key={activeMetricIndex}
                className="erp-suite-horizontal-body erp-fade-scale-in"
                onClick={nextMetric}
                title="Click to cycle next metric"
              >
                {/* Left: Badge, Big Stat & Title */}
                <div className="erp-suite-stat-col">
                  <div className="erp-suite-badge-tag">
                    <span className="erp-suite-badge-dot" />
                    <span>{activeMetric.badge}</span>
                  </div>
                  <div className="erp-suite-count-wrap">
                    <span className="erp-interactive-card-count">{activeMetric.count}</span>
                    <span className="erp-interactive-card-suffix">{activeMetric.suffix}</span>
                  </div>
                  <div className="erp-interactive-card-title">{activeMetric.title}</div>
                </div>

                {/* Right: Subnote & Concise Interactive Pills */}
                <div className="erp-suite-detail-col">
                  <p className="erp-suite-subnote">{activeMetric.subnote}</p>
                  <div className="erp-suite-pills-row">
                    {activeMetric.pills.map((pill, idx) => (
                      <span
                        key={idx}
                        className="erp-interactive-chip-clickable"
                        onClick={(e) => {
                          e.stopPropagation();
                          scrollToSection('modules-workspace');
                        }}
                      >
                        {pill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Card Footer Link: Direct jump to Live Test Drive */}
              <div
                className="erp-interactive-card-footer"
                onClick={() => scrollToSection('sandbox')}
                title="Click to jump to live test drive"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Play size={12} fill="#093991" color="#093991" />
                  <span className="erp-console-footer-text">
                    Instant interactive test drive — zero login required
                  </span>
                </div>
                <ChevronRight size={14} color="#093991" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoHero;
